import { useCallback, useLayoutEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { UfoCustomTimingWrapper } from '@/components/signup/utils/ufo-custom-timing-wrapper';
import { ufoEventRedirectToProduct } from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';

import ErrorBoundary from '../../components/percieved-provisioning/error-boundary/error-boundary';
import { useJiraTemplateChoice } from '../../hooks/use-jira-template-choice';
import { useScreenManager } from '../../hooks/use-screen-manager';

import { WorkStyleChoiceLoadingState } from './work-style-choice-loading-state';
import { WorkStyleChoiceView } from './work-style-choice-view';

const WorkStyleChoiceViewManager = () => {
	const { skipScreen } = useScreenManager();
	const { loadingUserSegmentData, shouldShowJiraTemplateChoiceScreen, setTemplateChoiceError } =
		useJiraTemplateChoice();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { products, edition, teamType } = useSearchParameters();

	useLayoutEffect(() => {
		if (!shouldShowJiraTemplateChoiceScreen && !loadingUserSegmentData) {
			skipScreen();
		}
	}, [loadingUserSegmentData, shouldShowJiraTemplateChoiceScreen, skipScreen]);

	const onError = useCallback(
		(error: Error) => {
			createAnalyticsEvent({
				actionSubject: 'confirmationPageScreen',
				action: 'failed',
				actionSubjectId: 'WorkStyleChoiceScreen',
				attributes: {
					products,
					edition,
					teamType,
				},
			}).fire();
			setTemplateChoiceError(error);
		},
		[createAnalyticsEvent, edition, products, setTemplateChoiceError, teamType],
	);

	if (loadingUserSegmentData) {
		return <WorkStyleChoiceLoadingState />;
	}

	if (!shouldShowJiraTemplateChoiceScreen) {
		return null;
	}

	return (
		<ErrorBoundary onError={onError}>
			<WorkStyleChoiceView />
		</ErrorBoundary>
	);
};

export const WorkStyleChoiceUfoWrapper = () => (
	<UfoCustomTimingWrapper
		ufoEvent={ufoEventRedirectToProduct}
		ufoCustomTimingKey={UfoCustomTimingKey.JIRA_WORK_STYLE_CHOICE_DWELL_TIME}
	>
		<WorkStyleChoiceViewManager />
	</UfoCustomTimingWrapper>
);
